import React, { useState, useMemo, useEffect } from 'react';
import Switch from 'react-switch';
import { toUpper } from 'lodash';
import { Colors } from '../../themes/Colors';
import TextBase from '../text';
import { translate } from '../../helper';
import { SubscriptionCard } from './index';
import ButtonGradient from '../button-gradient';
import { resolveCssVarValue } from '../../utils/CommonFunction';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import SaveIcon from '../../assets/image/svg/save-25.svg';

export const PlansGrid = ({
  plans = [],
  initialIsYearly = false,
  isYearly: controlledIsYearly,
  defaultSelectedKey = 'growth',
  onPurchase = () => { },
  onIsYearlyChange,
  currentPlanKey,
  headerTitle,
  buttonText1,
  buttonText2,
  onButton1Click = () => { },
  onButton2Click = () => { },
  style = {},
  buttonsRowReverse = false,
}) => {
    // Plan color mapping
    const getPlanColor = (planKey) => {
      const colorMap = {
        'free trial': '#6B7280',
        'starter': '#3B82F6',
        'growth': '#8B5CF6',
        'pro': '#EC4899',
      };
      return colorMap[planKey?.toLowerCase()] || '#3B82F6';
    };

    // Plan badge mapping
    const getPlanBadge = (planKey, isLast) => {
      const badgeMap = {
        'growth': 'MOST POPULAR',
        'pro': 'BEST VALUE',
      };
      return badgeMap[planKey?.toLowerCase()] || '';
    };
  const [isYearlyState, setIsYearlyState] = useState(initialIsYearly);
  const isYearly = typeof controlledIsYearly === 'boolean' ? controlledIsYearly : isYearlyState;
  const [selectedKey, setSelectedKey] = useState(defaultSelectedKey);
  const displayedPlans = useMemo(() => {
    const filtered = plans.filter(p => {
      if (!currentPlanKey) return true;
      const isCurrent = (p.key || '').toLowerCase().trim() === currentPlanKey.toLowerCase().trim();
      return isYearly ? true : !isCurrent;
    });
    return filtered.map((p) => {
      const monthObj = p.monthly && typeof p.monthly === 'object' ? p.monthly : null;
      const yearObj = p.yearly && typeof p.yearly === 'object' ? p.yearly : null;
      const selectedIntervalObj = isYearly ? yearObj : monthObj;

      const price = selectedIntervalObj
        ? Number(selectedIntervalObj.price || selectedIntervalObj.price === 0 ? selectedIntervalObj.price : 0)
        : (isYearly ? p.yearly : p.monthly);

      const ordersIncluded = selectedIntervalObj
        ? Number(selectedIntervalObj.free_order_limit || 0)
        : (p.ordersIncluded ?? 0);

      const messagesIncluded = selectedIntervalObj
        ? Number(selectedIntervalObj.free_sms_limit || 0)
        : (p.messagesIncluded ?? 0);

      const addlOrderPriceRaw = selectedIntervalObj
        ? (selectedIntervalObj.order_price ?? selectedIntervalObj.orderPrice ?? selectedIntervalObj.addlOrderPrice)
        : (p.addlOrderPrice ?? null);
      const addlMessagePriceRaw = selectedIntervalObj
        ? (selectedIntervalObj.sms_price ?? selectedIntervalObj.smsPrice ?? selectedIntervalObj.addlMessagePrice)
        : (p.addlMessagePrice ?? null);

      const addlOrderPrice = addlOrderPriceRaw == null ? null : Number(addlOrderPriceRaw);
      const addlMessagePrice = addlMessagePriceRaw == null ? null : Number(addlMessagePriceRaw);

      const paymentId = selectedIntervalObj ? selectedIntervalObj.id : (p.paymentId ?? null);

      const discount = selectedIntervalObj
        ? Number(selectedIntervalObj.discount || 0)
        : (p.discount ?? 0);

      return {
        ...p,
        price,
        interval: isYearly ? 'year' : 'month',
        ordersIncluded,
        messagesIncluded,
        addlOrderPrice,
        addlMessagePrice,
        paymentId,
        discount,
      };
    });
  }, [plans, isYearly, currentPlanKey]);

  const selectedPlan = useMemo(() => displayedPlans.find(p => p.key === selectedKey) || displayedPlans[0], [displayedPlans, selectedKey]);
  const yearlyDiscountPercent = useMemo(() => {
    const allDiscounts = plans
      .map((plan) => {
        const yearlyObj = plan?.yearly && typeof plan?.yearly === 'object' ? plan.yearly : null;
        const rawDiscount = yearlyObj ? yearlyObj.discount : plan?.discount;
        const value = Number(rawDiscount || 0);
        return Number.isFinite(value) && value > 0 ? value : 0;
      })
      .filter((value) => value > 0);

    return allDiscounts.length > 0 ? Math.max(...allDiscounts) : 0;
  }, [plans]);
  const yearlyDiscountText = yearlyDiscountPercent.toFixed(2).replace(/\.00$/, '');

  useEffect(() => {
    if (displayedPlans.length === 0) return;
    const exists = displayedPlans.some(p => p.key === selectedKey);
    if (!exists) {
      setSelectedKey(displayedPlans[0].key);
    }
  }, [displayedPlans, selectedKey]);
  const [switchSize, setSwitchSize] = useState({
    height: resolveCssVarValue('--fs-xl') * 1.48584905,
    width: resolveCssVarValue('--fs-xl') * 2.40566037,
    handleDiameter: resolveCssVarValue('--fs-xl') * 1.1320754,
  });

  useEffect(() => {
    const handleResize = () => {
      setSwitchSize({
        height: resolveCssVarValue('--fs-xl') * 1.48584905,
        width: resolveCssVarValue('--fs-xl') * 2.40566037,
        handleDiameter: resolveCssVarValue('--fs-xl') * 1.1320754,
      });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div style={{ width: '100%', ...style }}>
      <div
        style={{
          display: 'flex',
          justifyContent: headerTitle ? 'space-between' : 'center',
          alignItems: 'center',
          marginBottom: 'calc(var(--sp-lg) * 1.4210526)',
          flexWrap: 'wrap'
        }}
      >
        {headerTitle && (
          <TextBase
            fontWeight={600}
            size={'calc(var(--fs-xl) * 1.0526315)'}
            color={Colors.black}
          >
            {headerTitle}
          </TextBase>
        )}
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <TextBase
            style={{ opacity: isYearly ? 0.5 : 1 }}
            fontWeight={500}
            size={'calc(var(--fs-xs) * 1.6842105)'}
            color={Colors.black}
          >
            {translate('monthly') || 'Monthly'}
          </TextBase>
          <div className="react-switch" style={{ margin: `0px calc(var(--sp-md2) * 1.50943)` }}>
            <Switch
              key={switchSize.handleDiameter}
              checked={isYearly}
              onColor={Colors.primary}
              offColor="#E5E7EB"
              checkedHandleIcon={<div />}
              inputMode="none"
              height={switchSize.height}
              width={switchSize.width}
              handleDiameter={switchSize.handleDiameter}
              onChange={() => {
                const next = !isYearly;
                if (typeof controlledIsYearly !== 'boolean') {
                  setIsYearlyState(next);
                }
                if (typeof onIsYearlyChange === 'function') onIsYearlyChange(next);
              }}
              uncheckedIcon={<div />}
              checkedIcon={<div />}
            />
          </div>
          <div style={{ position: 'relative' }}>
            <TextBase
              style={{ opacity: isYearly ? 1 : 0.5 }}
              fontWeight={500}
              size={'calc(var(--fs-xs) * 1.6842105)'}
              color={Colors.black}
            >
              {translate('yearly') || 'Yearly'}
            </TextBase>

            {yearlyDiscountPercent > 0 && (
              <div
                style={{
                  position: 'absolute',
                  top: 'calc(var(--sp-md2) * -4.210526)',
                  left: 'calc(var(--sp-lg) * 2.32075)',
                  width: 'calc(var(--fs-hero) * 1.7368421)',
                  height: 'calc(var(--fs-hero) * 1.7368421)',
                  pointerEvents: 'none',
                  zIndex: 2,
                  opacity: isYearly ? 1 : 0.5,
                  transition: 'opacity 0.3s ease',
                }}
              >
                <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      whiteSpace: 'nowrap',
                      fontSize: 'calc(var(--fs-xs) * 1.32075)',
                      position: 'absolute',
                      bottom: 'calc(var(--sp-lg) * 2.5)',
                      left: 'calc(var(--sp-xxs) * 1.2)',
                      padding: 'calc(var(--sp-xxs) * 0.7) calc(var(--sp-xxs) * 1.3)',
                      borderRadius: 'calc(var(--fs-hero)*23.56132075)',
                      transform: 'rotate(-12deg)',
                      fontWeight: 700,
                      color: '#ffffff',
                      zIndex: 9999,
                      background: isYearly 
                        ? 'linear-gradient(135deg, #FFB81C 0%, #926002 100%)' 
                        : 'linear-gradient(135deg, #E0E0E0 0%, #D0D0D0 100%)',
                      boxShadow: isYearly 
                        ? '0 4px 12px rgba(255, 184, 28, 0.3)' 
                        : '0 2px 6px rgba(0, 0, 0, 0.1)',
                      letterSpacing: '0.5px',
                    }}
                  >
                    {(translate('saveMoney') || 'Save')}&nbsp;{yearlyDiscountText}%
                  </span>
                  <img
                  src={SaveIcon}
                    style={{
                      position: 'absolute',
                      bottom: 'calc(var(--sp-md2) * 0.8421052)',
                      left: 'calc(var(--sp-md2) * 1.32075)',
                      color: Colors.black,
                      height: 'calc(var(--fs-hero)*1.650943)',
                      width: 'calc(var(--fs-hero)*1.650943)',
                      opacity: isYearly ? 1 : 0.4,
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>


      <div style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${displayedPlans.length || 1}, minmax(calc(var(--fs-hero)*6.842105), 1fr))`,
        gap: 'calc(var(--sp-lg) * 1.2631579)',
        flexWrap: 'wrap',
        alignItems: 'stretch',
        minHeight: 'calc(var(--fs-hero) * 3.1578947)',
      }}>
        {displayedPlans.length === 0 && !isYearly && currentPlanKey ? (
          <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'calc(var(--sp-md2) * 1.6842105)', border: '1px dashed #D0D5DD', borderRadius: 'calc(var(--sp-md) * 1.2030075)' }}>
            <TextBase fontWeight={500} size={'calc(var(--fs-xs) * 1.473684)'} color={Colors.black} style={{ textAlign: 'center' }}>
              {translate('highestMonthlyPlanSwitchToYearly') || 'You are already on the highest available monthly plan. Switch to yearly to view yearly pricing.'}
            </TextBase>
          </div>
        ) : null}
        {displayedPlans.map((p) => {
          const orderPriceNum = p.addlOrderPrice == null ? null : Number(p.addlOrderPrice);
          const msgPriceNum = p.addlMessagePrice == null ? null : Number(p.addlMessagePrice);

          let additionalItems = (orderPriceNum == 0 && msgPriceNum == 0)
            ? [translate('negotiatedBulkRates') || 'Negotiated bulk rates for enterprise-level usage']
            : [
              orderPriceNum != null ? `$${orderPriceNum.toFixed(2)} / Order` : null,
              msgPriceNum != null ? `$${msgPriceNum.toFixed(2)} / SMS` : null,
            ].filter(Boolean);

          if (!additionalItems.length && orderPriceNum == null && msgPriceNum == null) {
            additionalItems = [translate('negotiatedBulkRates') || 'Negotiated bulk rates for enterprise-level usage'];
          }

          if (additionalItems.length === 1) {
            additionalItems.push(translate('negotiatedBulkRatesSpacer') || '\u00A0');
          }

          return (
            <SubscriptionCard
              key={p.key}
              variant="modern"
              isYearly={isYearly}
              highlight={p.key === selectedKey}
              selectable
              onSelect={() => setSelectedKey(p.key)}
              title={p.title}
              subTitle={p.description}
              badgeText={p.badgeText || getPlanBadge(p.key)}
              planColor={getPlanColor(p.key)}
              planKey={p.key}
              includedItems={[
                `${(p.ordersIncluded ?? 0).toLocaleString()} Orders`,
                `${(p.messagesIncluded ?? 0).toLocaleString()} SMS Messages`,
              ]}
              additionalItems={additionalItems}
              showEmpty
              hideCTA
              labelButton={toUpper(translate('purchase') || 'PURCHASE')}
              subscription={{
                price: p.price,
                recurring_interval: p.interval,
                discount: p.discount,
                paymentId: p.paymentId,
              }}
              style={{ width: '100%', maxWidth: 'unset' }}
            />
          );
        })}
      </div>

      <div
        style={{
          marginTop: 'calc(var(--sp-lg) * 1.2631578)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: buttonsRowReverse ? 'center' : 'space-between',
          flexDirection: buttonsRowReverse ? 'row-reverse' : 'row',
          gap: 'calc(var(--sp-md2) * 1.6842105)'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'calc(var(--sp-md2) * 1.2631578)' }}>
          {buttonText1 && (
            <ButtonGradient
              title={buttonText1}
              backgroundColor={'rgba(255, 255, 255, 1)'}
              textStyle={{ color: Colors.black, fontSize: 'calc(var(--fs-xs) * 1.578947)', fontWeight: 600 }}
              style={{ padding: 'calc(var(--sp-md2) * 1.3684210) calc(var(--sp-lg) * 1.1052631)', width: 'auto', border: '1px solid #000' }}
              onClick={() => {
                onButton1Click();
              }}
              disabled={!selectedPlan}
            />
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'calc(var(--sp-md2) * 1.2631578)' }}>
          <ButtonGradient
            style={{ padding: 'calc(var(--sp-md2) * 1.3684210) calc(var(--sp-lg) * 1.1052631)', width: 'auto' }}
            title={buttonText2 || toUpper(translate('purchase') || 'PURCHASE')}
            textStyle={{ fontSize: 'calc(var(--fs-xs) * 1.578947)', fontWeight: 600 }}
            disabled={!selectedPlan}
            onClick={() => {
              if (!selectedPlan) return;
              const payload = {
                key: selectedPlan.key,
                title: selectedPlan.title,
                price: selectedPlan.price,
                interval: selectedPlan.interval,
                discount: selectedPlan.discount,
                paymentId: selectedPlan.paymentId,
                ordersIncluded: selectedPlan.ordersIncluded,
                messagesIncluded: selectedPlan.messagesIncluded,
                addlOrderPrice: selectedPlan.addlOrderPrice,
                addlMessagePrice: selectedPlan.addlMessagePrice,
                isYearly,
                rawSelected: isYearly ? (selectedPlan.yearly || null) : (selectedPlan.monthly || null),
                rawGroup: selectedPlan,
              };
              if (onButton2Click) return onButton2Click(payload);
              return onPurchase(payload);
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default PlansGrid;
